import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog } from 'primereact/confirmdialog';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { toastify } from '../../../utils/toast';
import { makeRequest2, makeRequest4 } from '../../../utils/makeRequest';
import { getFacilitiesURL } from '../../../utils/urls';
import Layout from '../component/layout';

function PowerMetersDashboard2() {
  const navigate = useNavigate();
  const [powerMeters, setPowerMeters] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPowerMeters, setFilteredPowerMeters] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [showModal2, setShowModal2] = useState(false);
  const [selectedPowerMeter, setSelectedPowerMeter] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const clientRef = useRef(null);

  // Config states
  const [manufacturers, setManufacturers] = useState([]);
  const [customerBands, setCustomerBands] = useState([]);
  const [facilities, setFacilities] = useState([]);

  const [meterTypes, setMeterTypes] = useState([
    { _id: '2-phase', name: '2 phase' },
    { _id: '3-phase', name: '3 phase' },
  ]);

  const [liveStatusOptions, setLiveStatusOptions] = useState([
    { _id: 'Online', name: 'Online' },
    { _id: 'Offline', name: 'Offline' },
    { _id: 'Faulty', name: 'Faulty' },
  ]);

  // File import dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  // NEW: Facility filter (dropdown)
  const [selectedFacility, setSelectedFacility] = useState('');

  // Base URLs
  const manufacturerBaseUrl = '/api/core/power_meter_management/controllers/manufacturers';
  const customerBandBaseUrl = '/api/core/power_meter_management/controllers/customer_band';
  const powerMeterUrl = '/power_meters/v1/api/acrel';
  const facilitiesBaseUrl = getFacilitiesURL;

  // Form data
  const [formData, setFormData] = useState({
    gatewayId: '',
    gatewayName: '',
    gatewayStatus: '',
    status: '',
    createdAt: '',
  });

  // --- Helpers for status badge colours ---
  const gatewayStatusColors = {
    Online: 'bg-success',
    Offline: 'bg-danger',
    Faulty: 'bg-warning',
    maintenance: 'bg-secondary',
  };

  const getGatewayStatusClass = (status) =>
    gatewayStatusColors[status] || 'bg-secondary';

  const statusTemplate = (rowData) => {
    const cls = getGatewayStatusClass(rowData.gatewayStatus);
    return (
      <span className={`badge ${cls}`}>
        {rowData.gatewayStatus || 'Unknown'}
      </span>
    );
  };

  const statusTemplate2 = (rowData) => {
    const statusColors = {
      active: 'success',
      Inactive: 'danger',
    };

    return (
      <span className={`badge bg-${statusColors[rowData.status] || 'secondary'}`}>
        {rowData.status || 'Unknown'}
      </span>
    );
  };

  // Realtime check
  const handleSubmit2 = async () => {
    const { gatewayId } = formData;
    if (!gatewayId) return alert('Please enter Gateway ID');

    setIsSubmitting(true);
    setCheckingStatus(true);
    setFormData((prev) => ({ ...prev, gatewayStatus: '', gatewayName: '' }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/check_acrel_status`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            gatewayId,
            timeout: 30000, // 30 seconds (optional)
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setFormData((prev) => ({
          ...prev,
          gatewayStatus: result.status || 'Unknown',
          gatewayName: result.gatewayName,
        }));
      } else {
        alert(result.error || 'Failed to get status');
        setFormData((prev) => ({ ...prev, gatewayStatus: 'Error' }));
      }
    } catch (error) {
      console.error('Error checking gateway status:', error);
      alert('Failed to connect to backend');
      setFormData((prev) => ({ ...prev, gatewayStatus: 'Error' }));
    } finally {
      setIsSubmitting(false);
      setCheckingStatus(false);
    }
  };

  const handleClose = () => {
    if (clientRef.current) clientRef.current.end();
    setFormData({
      gatewayId: '',
      gatewayName: '',
      gatewayStatus: '',
      status: '',
      createdAt: '',
    });
    setIsSubmitting(false);
    setCheckingStatus(false);
    setShowModal2(false);
  };

  // Fetch configs
  const fetchConfigurations = async () => {
    try {
      // Manufacturers
      try {
        const manufacturersRes = await makeRequest2(
          `${manufacturerBaseUrl}/get_all`,
          'GET'
        );
        if (manufacturersRes && (manufacturersRes.success || manufacturersRes.data)) {
          const manufacturersData = manufacturersRes.data || [];
          if (Array.isArray(manufacturersData)) {
            setManufacturers(manufacturersData);
          } else {
            console.warn('Manufacturers data is not an array:', manufacturersData);
            setManufacturers([]);
          }
        } else {
          setManufacturers([]);
        }
      } catch (err) {
        console.error('Error fetching manufacturers:', err);
        setManufacturers([]);
      }

      // Customer bands
      try {
        const customerBandsRes = await makeRequest2(
          `${customerBandBaseUrl}/get_all`,
          'GET'
        );
        if (customerBandsRes && (customerBandsRes.success || customerBandsRes.data)) {
          const customerBandsData = customerBandsRes.data || [];
          if (Array.isArray(customerBandsData)) {
            setCustomerBands(customerBandsData);
          } else {
            console.warn('Customer bands data is not an array:', customerBandsData);
            setCustomerBands([]);
          }
        } else {
          setCustomerBands([]);
        }
      } catch (err) {
        console.error('Error fetching customer bands:', err);
        setCustomerBands([]);
      }

      // Facilities
      try {
        const facilitiesRes = await makeRequest2(facilitiesBaseUrl, 'GET');
        if (facilitiesRes && facilitiesRes.success) {
          let facilitiesData = [];

          if (Array.isArray(facilitiesRes.data)) {
            facilitiesData = facilitiesRes.data;
          } else if (
            facilitiesRes.data &&
            Array.isArray(facilitiesRes.data.facilities)
          ) {
            facilitiesData = facilitiesRes.data.facilities;
          } else if (
            facilitiesRes.facilities &&
            Array.isArray(facilitiesRes.facilities)
          ) {
            facilitiesData = facilitiesRes.facilities;
          }

          setFacilities(facilitiesData);
        } else {
          setFacilities([]);
        }
      } catch (err) {
        console.error('Error fetching facilities:', err);
        setFacilities([]);
      }
    } catch (error) {
      console.error('Error in fetchConfigurations:', error);
      setManufacturers([]);
      setCustomerBands([]);
      setFacilities([]);
    }
  };

  // Fetch power meters
  const fetchPowerMeters = async () => {
    try {
      setLoading(true);
      const response = await makeRequest4(
        `${powerMeterUrl}/get_all_acrel_gateways`,
        'GET'
      );

      if (response && (response.success || response.data?.data !== undefined)) {
        let powerMetersData = [];

        if (Array.isArray(response.data?.data)) {
          powerMetersData = response.data.data;
        } else if (
          response.data &&
          Array.isArray(response.data.data?.powerMeters)
        ) {
          powerMetersData = response.data.data.powerMeters;
        } else if (response.powerMeters && Array.isArray(response.powerMeters)) {
          powerMetersData = response.powerMeters;
        } else if (response.data?.data && response.data.data.length === undefined) {
          powerMetersData = [];
        }

        if (Array.isArray(powerMetersData)) {
          setPowerMeters(powerMetersData);
          setFilteredPowerMeters(powerMetersData);
        } else {
          console.warn('Power meters data is not an array:', powerMetersData);
          setPowerMeters([]);
          setFilteredPowerMeters([]);
        }
      } else {
        toastify(response?.error || 'Failed to fetch power meters', 'error');
        setPowerMeters([]);
        setFilteredPowerMeters([]);
      }
    } catch (error) {
      console.error('Error fetching power gateways:', error);
      toastify('An error occurred while fetching power gateways', 'error');
      setPowerMeters([]);
      setFilteredPowerMeters([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchConfigurations();
      await fetchPowerMeters();
    };
    loadData();
  }, []);

  // Filter: search + facility
  useEffect(() => {
    let filtered = powerMeters;

    // Search filter
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (powerMeter) =>
          powerMeter.meterSerialNumber?.toLowerCase().includes(term) ||
          powerMeter.manufacturer?.toLowerCase().includes(term) ||
          powerMeter.gatewayId?.toLowerCase().includes(term)
      );
    }

    // Facility filter - matches powerMeter.gatewayName with facility name
    if (selectedFacility.trim() !== '') {
  const selectedName = selectedFacility.toLowerCase();

  filtered = filtered.filter((pm) => {
    const gwName = pm.gatewayName?.toLowerCase() || '';
    return gwName.includes(selectedName); // 👈 partial match instead of ===
  });

  console.log('facility filter ->', selectedName, filtered);
}

    setFilteredPowerMeters(filtered);
  }, [searchTerm, selectedFacility, powerMeters]);

  // Helper function: facility name from id
  const getFacilityName = (facilityId) => {
    const facility = facilities.find((f) => f._id === facilityId);
    return facility ? facility.name : 'Unknown Facility';
  };

  // File select handler
  const handleFileSelect = (event) => {
    setUploadedFile(event.files[0]);
  };

  // File upload handler
  const handleFileUpload = async () => {
    if (!uploadedFile) {
      toastify('Please select a file first', 'error');
      return;
    }

    const formDataObj = new FormData();
    formDataObj.append('file', uploadedFile);

    try {
      const response = await makeRequest2(
        `${powerMeterUrl}/import`,
        'POST',
        formDataObj,
        true
      );
      if (response.success) {
        toastify('Power meters imported successfully', 'success');
        fetchPowerMeters();
        setShowImportDialog(false);
      } else {
        toastify(response.error || 'Failed to import power meters', 'error');
      }
    } catch (error) {
      toastify('An error occurred while importing power meters', 'error');
    }
  };

  // Open edit modal
  const handleModalOpen = (powerMeter) => {
    if (powerMeter) {
      setSelectedPowerMeter(powerMeter);
      setFormData({
        gatewayId: powerMeter.gatewayId || '',
        gatewayName: powerMeter.gatewayName || '',
        gatewayStatus: powerMeter.gatewayStatus || '',
        status: powerMeter.status || '',
        createdAt: powerMeter.createdAt || '',
      });
    }
    setShowModal(true);
  };

  // Open realtime modal
  const handleModalOpen2 = (powerMeter) => {
    if (powerMeter) {
      setSelectedPowerMeter(powerMeter);
      setFormData({
        gatewayId: powerMeter.gatewayId || '',
        gatewayName: powerMeter.gatewayName || '',
        gatewayStatus: powerMeter.gatewayStatus || '',
        status: powerMeter.status || '',
        createdAt: powerMeter.createdAt || '',
      });
    }
    setShowModal2(true);
  };

  // Save edit
  const handleSubmit = async () => {
    if (!selectedPowerMeter?._id) return;

    setIsSubmitting(true);
    try {
      const response = await makeRequest4(
        `${powerMeterUrl}/update_acrel_gateway/${selectedPowerMeter._id}`,
        'POST',
        formData
      );

      if (response.success || response.message) {
        toastify('Power meter updated successfully', 'success');
        fetchPowerMeters();
        setShowModal(false);
      } else {
        toastify(response.error || 'Failed to update power meter', 'error');
      }
    } catch (error) {
      console.error('Error updating power meter:', error);
      toastify('An error occurred while updating the power meter', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Import dialog
  const ImportDialog = () => (
    <Dialog
      header="Import Power Meters"
      visible={showImportDialog}
      onHide={() => setShowImportDialog(false)}
      style={{ width: '50vw' }}
    >
      <div className="p-fluid">
        <div className="field mb-4">
          <h5>Upload CSV or Excel File</h5>
          <FileUpload
            mode="basic"
            accept=".csv,.xlsx,.xls"
            maxFileSize={1000000}
            chooseLabel="Choose File"
            className="mb-3"
            onSelect={handleFileSelect}
          />
          <small className="text-muted block">
            Allowed file types: .csv, .xlsx, .xls (Max size: 1MB)
          </small>
        </div>
        <div className="text-end mt-4">
          <Button
            label="Cancel"
            icon="pi pi-times"
            onClick={() => setShowImportDialog(false)}
            className="p-button-text mr-2"
          />
          <Button
            label="Import"
            icon="pi pi-check"
            onClick={handleFileUpload}
            disabled={!uploadedFile}
          />
        </div>
      </div>
    </Dialog>
  );

  return (
    <Layout>
      <ConfirmDialog />
      <div className="page-header">
        <ul className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/core/dashboard">Dashboard</Link>
          </li>
          <li className="breadcrumb-item">Power Gateway Management</li>
          <li className="breadcrumb-item active">Power Gateways</li>
        </ul>
      </div>

      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <div className="row">
                {/* Search */}
                <div className="col-3">
                  <div className="form-search">
                    <i className="ti ti-search"></i>
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search by serial number, manufacturer, or gateway ID"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* NEW: Facility Filter Dropdown (matches gatewayName) */}
                <div className="col-3">
                  <select
                    className="form-control"
                    value={selectedFacility}
                    onChange={(e) => setSelectedFacility(e.target.value)}
                  >
                    <option value="">All Facilities</option>
                    {facilities.map((f) => (
                      <option key={f._id} value={f.name}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* You can add more header actions (import, etc.) here if needed */}
              </div>
            </div>

            <div className="card-body">
              {/* Card grid */}
              <div className="row">
                {loading && (
                  <div className="col-12">
                    <p>Loading power gateways...</p>
                  </div>
                )}

                {!loading && filteredPowerMeters.length === 0 && (
                  <div className="col-12">
                    <p>No power gateways found.</p>
                  </div>
                )}

                {!loading &&
                  filteredPowerMeters.map((pm) => (
                    <div key={pm._id} className="col-md-4 col-sm-6 mb-3">
                      <div className="card h-100 shadow-sm">
                        <div className="card-body">
                          {/* Header: name + gateway status */}
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <h5 className="card-title mb-0">
                              {pm.gatewayName || 'Gateway'}
                            </h5>
                            <span
                              className={`badge ${getGatewayStatusClass(
                                pm.gatewayStatus
                              )}`}
                            >
                              {pm.gatewayStatus || 'Unknown'}
                            </span>
                          </div>

                          {/* Icon */}
                          <div className="mb-3">
                            <i
                              className="ti ti-device-ethernet"
                              style={{ fontSize: 24 }}
                            />
                          </div>

                          {/* Content */}
                          <p className="mb-1">
                            <strong>Gateway id:</strong> {pm.gatewayId}
                          </p>
                          <p className="mb-1">
                            <strong>Facility:</strong>{' '}
                            {getFacilityName(pm.facilityId)}
                          </p>
                          <p className="mb-1">
                            <strong>Manufacturer:</strong>{' '}
                            {pm.manufacturer || 'N/A'}
                          </p>
                          <p className="mb-1">
                            <strong>Type:</strong> {pm.type || 'N/A'}
                          </p>
                          <p className="mb-1">
                            <strong>Last Update:</strong>{' '}
                            {pm.lastUpdateDuration ||
                              (pm.updatedAt
                                ? new Date(pm.updatedAt).toLocaleString()
                                : 'N/A')}
                          </p>
                          <p className="mb-1">
                            <strong>Live Status</strong>
                          </p>
                          <p className="mb-3">
                            <strong>Updated:</strong>{' '}
                            {pm.liveStatusDuration || 'N/A'}
                          </p>

                          <div className="mb-3">
                            {statusTemplate2(pm)}
                          </div>

                          {/* Actions */}
                          <div className="d-flex gap-2">
                            <Button
                              icon="ti ti-edit"
                              label="Edit"
                              className="p-button-rounded p-button-info"
                              onClick={() => handleModalOpen(pm)}
                            />
                            <Button
                              icon="ti ti-eye"
                              label="Realtime"
                              className="p-button-rounded p-button-secondary"
                              onClick={() => handleModalOpen2(pm)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog
        header="Edit Power Gateway"
        visible={showModal}
        onHide={() => setShowModal(false)}
        style={{ width: '50vw' }}
      >
        <div className="p-fluid">
          <div className="field mb-3">
            <label className="form-label">
              Gateway ID<span style={{ color: 'red' }}>*</span>
            </label>
            <InputText
              value={formData.gatewayId}
              onChange={(e) =>
                setFormData({ ...formData, gatewayId: e.target.value })
              }
              className="form-control"
              disabled={isSubmitting}
            />
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Gateway Name<span style={{ color: 'red' }}>*</span>
            </label>
            <InputText
              value={formData.gatewayName}
              onChange={(e) =>
                setFormData({ ...formData, gatewayName: e.target.value })
              }
              className="form-control"
              disabled={isSubmitting}
            />
          </div>

          <div className="field mb-3">
            <label className="form-label">
              Status<span style={{ color: 'red' }}>*</span>
            </label>
            <select
              className="form-control"
              value={formData.gatewayStatus || ''}
              onChange={(e) =>
                setFormData({ ...formData, gatewayStatus: e.target.value })
              }
              disabled={isSubmitting}
            >
              <option value="">Select Status</option>
              {liveStatusOptions.map((s) => (
                <option key={s._id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="text-end mt-4">
          <Button
            label={isSubmitting ? 'Saving...' : 'Save'}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="p-button-primary"
          />
        </div>
      </Dialog>

      {/* Realtime dialog */}
      <Dialog
        header="Get Realtime Power Gateway Status"
        visible={showModal2}
        onHide={handleClose}
        style={{ width: '50vw' }}
      >
        <div className="p-fluid">
          <div className="form-control bg-light">
            <div className="field mb-3">
              <label className="form-label">Status</label>
              <div>
                {formData.gatewayStatus ? (
                  statusTemplate(formData)
                ) : (
                  <p className="text-info">
                    ⏳ Checking status... Please wait up to 30 seconds.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="text-end mt-4">
          <Button
            label={isSubmitting ? 'Checking...' : 'Check Status'}
            onClick={handleSubmit2}
            disabled={isSubmitting}
            className="p-button-primary"
          />
        </div>
      </Dialog>

      <ImportDialog />
    </Layout>
  );
}

export default PowerMetersDashboard2;
